/**
 * How a restored draft is described to the user, shared by every wizard that has one.
 *
 * Kept here rather than in one feature because a second copy is how this spec's whole
 * problem started: the image-retention decision was pasted into six form files and had to
 * be fixed three separate times.
 */

export interface DraftDescription {
  /** "12 minutes ago", "yesterday" — the banner never says just "restored". */
  age: string;
  /** Human list of what came back: "simulation name, goal, 30 questions". */
  restored: string[];
  /** True when the draft references images the in-memory store can no longer supply. */
  imagesNeedReupload: boolean;
}

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * Age in the units a person would actually use. Never returns a negative or absolute
 * timestamp: a draft written just before a clock correction would otherwise render
 * "restored from -3 minutes ago", which reads as corruption.
 */
export function describeAge(ageMs: number): string {
  const ms = Math.max(0, ageMs);
  if (ms < MINUTE) return 'a few seconds ago';
  if (ms < HOUR) {
    const m = Math.round(ms / MINUTE);
    return `${m} minute${m === 1 ? '' : 's'} ago`;
  }
  if (ms < DAY) {
    const h = Math.round(ms / HOUR);
    return `${h} hour${h === 1 ? '' : 's'} ago`;
  }
  const d = Math.round(ms / DAY);
  return d === 1 ? 'yesterday' : `${d} days ago`;
}
